/*
 * Hub Onboarding — first-visit seen flag (Feb 2026).
 *
 * A tiny versioned localStorage flag drives whether the animated
 * onboarding popup should mount for a given browser. Bumping the
 * version constant (`ONBOARDING_SEEN_KEY`) forces every returning
 * visitor to see the tutorial again — useful when the content
 * meaningfully changes.
 *
 * The rendering component NEVER marks itself as seen just because it
 * appeared. It calls `markOnboardingSeen()` when the user explicitly
 * dismisses (X, Skip, Escape) or completes the tutorial. This split
 * is what the focused test verifies.
 */
export const ONBOARDING_SEEN_KEY = "prophets_hub_onboarding_seen_v1";

/**
 * Custom-event name the "HOW IT WORKS" button dispatches on `window`
 * to force-open the tutorial. Manual opens always bypass the
 * seen-flag gate — the flag controls automatic first-visit opens
 * only. Exported here (not in the component) so the button can
 * import it without pulling in React or the router.
 */
export const HUB_ONBOARDING_OPEN_EVENT = "hub-onboarding:open";

function _storage() {
  try { return window.localStorage; } catch { return null; }
}

export function hasSeenOnboarding() {
  const s = _storage();
  if (!s) return true;   // Fail closed — never nag if storage is off.
  try { return s.getItem(ONBOARDING_SEEN_KEY) === "1"; }
  catch { return true; }
}

export function markOnboardingSeen() {
  const s = _storage();
  if (!s) return;
  try { s.setItem(ONBOARDING_SEEN_KEY, "1"); } catch { /* no-op */ }
}

export function resetOnboardingSeen() {
  // Provided for future admin/QA use — the runtime never calls this.
  const s = _storage();
  if (!s) return;
  try { s.removeItem(ONBOARDING_SEEN_KEY); } catch { /* no-op */ }
}

/**
 * True on any user-facing Hub path where the tutorial may auto-mount.
 * Excludes the public landing page (`/`), the admin subtree
 * (`/admin/*`), and every non-Hub route — the tutorial is a Hub
 * feature only. The manual "HOW IT WORKS" button bypasses this
 * check because manual opens are always allowed.
 */
export function isEligibleOnboardingPath(pathname) {
  if (!pathname) return false;
  if (pathname.startsWith("/admin")) return false;
  return pathname === "/hub" || pathname.startsWith("/hub/");
}
